// HUD de la pantalla de partida. Presentación pura: emite acciones por callbacks.
import { EVENTS } from '../utils/constants.js';
import { formatTime } from '../utils/helpers.js';
import { avatarHTML } from './ProfileUI.js';

// Iconos SVG de los botones de acción (estilo "Nocturno Neón"). Heredan el color
// del botón (currentColor = --acc), así que brillan en su color de acento.
const IC = {
  reset: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><path d="M3 3v6h6"/><path d="M3.5 9a9 9 0 1 1-1.2 6"/></svg>',
  undo: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><path d="M9 7 4 12l5 5"/><path d="M4 12h11a5 5 0 0 1 0 10"/></svg>',
  pass: '<svg viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" stroke-width="1" stroke-linejoin="round"><path d="M5 5l8 7-8 7z"/><rect x="16" y="5" width="3" height="14" rx="1.2"/></svg>',
  confirm: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3.2" stroke-linecap="round" stroke-linejoin="round"><path d="M20 6 9 17l-5-5"/></svg>',
  pause: '<svg viewBox="0 0 24 24" fill="currentColor"><rect x="6" y="5" width="4.4" height="14" rx="1.4"/><rect x="13.6" y="5" width="4.4" height="14" rx="1.4"/></svg>',
  help: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="9.2"/><path d="M9.2 9.3a2.9 2.9 0 0 1 5.5 1.1c0 1.9-2.7 2-2.7 3.9"/><circle cx="12" cy="17.4" r="0.5" fill="currentColor" stroke="none"/></svg>',
};

export class GameHUD {
  constructor(root, { bus, game, actions }) {
    this.root = root;
    this.bus = bus;
    this.game = game;
    this.actions = actions; // { confirm, undo, reset, pass, pause }
    this.el = document.createElement('div');
    this.el.className = 'screen game-hud hidden';
    this.root.appendChild(this.el);
    this._build();
    this._wireEvents();
  }

  _build() {
    this.el.innerHTML = `
      <div class="hud-top">
        <button class="pause-btn pause-img-btn act-neon" style="--acc:#5fe0d0" data-act="pause" aria-label="Pausa" title="Pausa">
          <span class="act-ic">${IC.pause}</span>
        </button>
        <div class="players-panel" id="players-panel"></div>
        <div class="hud-top-right">
          <div class="turn-timer hidden" id="turn-timer" role="timer" aria-label="Tiempo restante">
            <svg viewBox="0 0 48 48" class="timer-ring">
              <circle class="timer-track" cx="24" cy="24" r="20"></circle>
              <circle class="timer-progress" cx="24" cy="24" r="20"></circle>
            </svg>
            <span class="timer-text" id="timer-text">60</span>
          </div>
          <div class="bag-chip" id="bag-chip" title="Fichas que quedan en la bolsa">
            <span class="bag-ic">🎒</span><span class="bag-num" id="bag-count">0</span>
          </div>
        </div>
      </div>

      <button class="side-toggle" id="side-toggle" aria-label="Ver palabras en la mesa" title="Palabras en la mesa">
        <span class="st-ic">📋</span><span class="st-count" id="side-toggle-count">0</span>
      </button>
      <aside class="side-panel" id="side-panel" aria-live="polite">
        <div class="side-title"><span class="dot"></span> Palabras en la mesa <span class="side-count" id="side-count"></span>
          <button class="side-close" id="side-close" aria-label="Cerrar">✕</button></div>
        <div class="words-list" id="words-list"><p class="hint">Arrastra cartas de tu mano a la mesa para construir palabras.</p></div>
        <div class="turn-score" id="turn-score"></div>
      </aside>

      <div class="validation-banner hidden" id="validation-banner" role="status"></div>

      <div class="turn-announce hidden" id="turn-announce" aria-live="polite">
        <span class="ta-avatar" id="ta-avatar"></span>
        <span class="ta-text" id="ta-text"></span>
      </div>


      <div class="hud-actions">
        <div class="action-bar action-bar-img">
          <button class="btn act-btn act-img-btn act-neon" style="--acc:#f0785a" data-act="reset" title="Reiniciar turno (borra la jugada)" aria-label="Reiniciar">
            <span class="act-ic">${IC.reset}</span></button>
          <button class="btn act-btn act-img-btn act-neon" style="--acc:#5fe0d0" data-act="undo" title="Deshacer (Ctrl+Z)" aria-label="Deshacer">
            <span class="act-ic">${IC.undo}</span></button>
          <button class="btn act-btn act-img-btn act-neon" style="--acc:#b98cf5" data-act="pass" title="Robas 1 ficha y pasas el turno" aria-label="Pasar">
            <span class="act-ic">${IC.pass}</span></button>
          <button class="btn act-btn act-img-btn act-neon act-confirm btn-confirm" style="--acc:#a3e15c" data-act="confirm" title="Confirmar (Enter)" aria-label="Continuar">
            <span class="act-ic">${IC.confirm}</span></button>
        </div>
      </div>

      <button class="hud-help hud-help-img act-neon" style="--acc:#f2cb4d" data-act="help" aria-label="Ayuda" title="¿Para qué sirve cada botón?">
        <span class="act-ic">${IC.help}</span></button>
    `;

    this.el.querySelector('[data-act="pause"]').addEventListener('click', () => this.actions.pause());
    this.el.querySelector('[data-act="reset"]').addEventListener('click', () => this.actions.reset());
    this.el.querySelector('[data-act="undo"]').addEventListener('click', () => this.actions.undo());
    this.el.querySelector('[data-act="pass"]').addEventListener('click', () => this.actions.pass());
    this.el.querySelector('[data-act="confirm"]').addEventListener('click', () => this.actions.confirm());
    this.el.querySelector('[data-act="help"]').addEventListener('click', () => this.actions.help?.());

    // Panel de palabras desplegable (botón lateral izquierdo).
    this.sidePanel = this.el.querySelector('#side-panel');
    this.sideToggle = this.el.querySelector('#side-toggle');
    this.sideToggleCount = this.el.querySelector('#side-toggle-count');
    // El botón abre una VENTANA EMERGENTE con las palabras (mejor que el panel deslizante).
    this.sideToggle.addEventListener('click', () => this.actions.showWords?.());
    this.el.querySelector('#side-close').addEventListener('click', () => this.sidePanel.classList.remove('open'));

    this.playersPanel = this.el.querySelector('#players-panel');
    this.wordsList = this.el.querySelector('#words-list');
    this.turnScoreEl = this.el.querySelector('#turn-score');
    this.banner = this.el.querySelector('#validation-banner');
    this.timerEl = this.el.querySelector('#turn-timer');
    this.timerText = this.el.querySelector('#timer-text');
    this.timerProgress = this.el.querySelector('.timer-progress');
    this.sideCount = this.el.querySelector('#side-count');
    this.bagCount = this.el.querySelector('#bag-count');
    this.turnAnnounce = this.el.querySelector('#turn-announce');
    this.taText = this.el.querySelector('#ta-text');
    this.taAvatar = this.el.querySelector('#ta-avatar');
    this._thinkingId = null;
    this._ringC = 2 * Math.PI * 20; // circunferencia del anillo del reloj
    this._pcRingC = 2 * Math.PI * 19; // anillo del reloj DENTRO de la ficha del jugador
    if (this.timerProgress) {
      this.timerProgress.style.strokeDasharray = String(this._ringC);
    }
    // Recalcula la altura reservada del panel de jugadores al girar / redimensionar.
    this._onResize = () => this._reservePlayersHeight();
    window.addEventListener('resize', this._onResize);
    window.addEventListener('orientationchange', this._onResize);
  }

  _wireEvents() {
    this._offState = this.bus.on(EVENTS.STATE_CHANGED, () => this.refresh());
    this._offValid = this.bus.on(EVENTS.WORD_VALIDATED, (v) => this._showValidation(v));
    this._offTurn = this.bus.on(EVENTS.TURN_START, () => { this.announceTurn(); this.refresh(); });
  }

  /** Anuncio grande de cambio de turno: "TU TURNO" o "Turno de X". */
  announceTurn() {
    if (this.el.classList.contains('hidden') || !this.game.state) return;
    const p = this.game.activePlayer;
    const self = this.game.isSelfTurn ? this.game.isSelfTurn() : !p.isAI;
    this.taAvatar.innerHTML = avatarHTML(p);
    this.taText.textContent = self ? '¡TU TURNO!' : `Turno de ${p.name}`;
    this.turnAnnounce.classList.toggle('human', self);
    this.turnAnnounce.classList.toggle('ai', !self);
    this.turnAnnounce.classList.remove('hidden', 'show');
    // reinicia la animación
    void this.turnAnnounce.offsetWidth;
    this.turnAnnounce.classList.add('show');
    clearTimeout(this._annT);
    this._annT = setTimeout(() => this.turnAnnounce.classList.add('hidden'), 2200);
  }

  /** Marca a un jugador (IA) como "pensando". */
  setThinking(playerId) {
    this._thinkingId = playerId;
    this.refresh();
  }

  refresh() {
    if (this.el.classList.contains('hidden')) return;
    this._renderPlayers();
    this._reservePlayersHeight();
    this._renderWords();
    this._updateActionState();
    if (this.bagCount) this.bagCount.textContent = String(this.game.state?.bag?.length ?? 0);
  }

  /** El panel de jugadores reserva SIEMPRE la altura de 4 jugadores (aunque haya 2 o 3),
   *  para que la disposición no cambie según el número de rivales. Solo aplica al layout
   *  en COLUMNA (móvil apaisado), donde apilar cambia la altura. */
  _reservePlayersHeight() {
    const panel = this.playersPanel;
    if (!panel) return;
    const column = typeof window !== 'undefined'
      && window.matchMedia?.('(max-height: 480px) and (orientation: landscape)').matches;
    if (!column) { panel.style.minHeight = ''; return; }
    const card = panel.querySelector('.player-card');
    if (!card) { panel.style.minHeight = ''; return; }
    const h = card.getBoundingClientRect().height;
    if (!h) return;
    const gap = parseFloat(getComputedStyle(panel).gap) || 5;
    panel.style.minHeight = `${Math.round(h * 4 + gap * 3)}px`; // 4 tarjetas + 3 huecos
  }

  _renderPlayers() {
    const s = this.game.state;
    this.playersPanel.innerHTML = this.game.players
      .map((p, i) => {
        const active = i === s.activePlayerIndex;
        const avatar = avatarHTML(p);
        const isSelf = p.id === (this.game.selfPlayer?.id);
        const thinking = this._thinkingId === p.id && active;
        const meta = thinking
          ? '<span class="pc-thinking"><span class="dots"><i></i><i></i><i></i></span></span>'
          : `<span class="pc-score">${p.score}<i>pts</i></span><span class="pc-tiles" title="${p.rack.size} fichas en mano">🂠 ${p.rack.size}</span>`;
        return `<div class="player-card ${active ? 'active' : ''} ${p.finished ? 'done' : ''} ${thinking ? 'thinking' : ''}">
          <div class="pc-avatar">${avatar}</div>
          <div class="pc-info">
            <div class="pc-name"><span class="pc-name-txt">${p.name}</span>${isSelf ? '<span class="pc-you">TÚ</span>' : ''}</div>
            <div class="pc-meta">${meta}</div>
          </div>
          <div class="pc-timer" aria-hidden="true">
            <div class="pc-clock">
              <svg class="pc-timer-ring" viewBox="0 0 44 44">
                <circle class="pctr-track" cx="22" cy="22" r="19"></circle>
                <circle class="pctr-prog" cx="22" cy="22" r="19" style="stroke-dasharray:${this._pcRingC}"></circle>
              </svg>
              <span class="pc-timer-num"></span>
            </div>
          </div>
        </div>`;
      })
      .join('');
  }

  _renderWords() {
    const words = this.game.board.words;
    if (this.sideCount) this.sideCount.textContent = words.length ? String(words.length) : '';
    if (this.sideToggleCount) this.sideToggleCount.textContent = String(words.length);
    // El botón desplegable resalta si hay alguna palabra inválida.
    if (this.sideToggle) {
      const anyBad = words.some((w) => !this.game.validator.validateWord(w.tiles).valid);
      this.sideToggle.classList.toggle('has-bad', anyBad);
    }
    if (!words.length) {
      this.sidePanel?.classList.remove('open'); // sin palabras: colapsa
      this.wordsList.innerHTML = '<p class="hint">Arrastra cartas de tu mano a la mesa para construir palabras.</p>';
      return;
    }
    this.wordsList.innerHTML = words
      .map((w) => {
        const res = this.game.validator.validateWord(w.tiles);
        const text = this.game.board.wordText(w).toUpperCase();
        return `<div class="word-row ${res.valid ? 'ok' : 'bad'}">
          <span class="wr-mark">${res.valid ? '✓' : '✕'}</span>
          <span class="wr-text">${text || '—'}</span>
        </div>`;
      })
      .join('');
  }

  _showValidation(validation) {
    if (!validation) return;
    if (validation.valid || !validation.firstError) {
      this.banner.classList.add('hidden');
      return;
    }
    if (!validation.changed) {
      this.banner.classList.add('hidden');
      return;
    }
    // Mensaje CORTO por código (el motivo largo ocupaba demasiado).
    const SHORT = {
      dictionary: 'No está en el diccionario',
      minlength: 'Palabra muy corta',
      chars: 'Caracteres no válidos',
      wildcard: 'Asigna una letra al comodín',
      prefix: 'Prefijo mal colocado',
      suffix: 'Sufijo mal colocado',
      empty: '',
    };
    const code = validation.firstError.code;
    const msg = (code in SHORT) ? SHORT[code] : validation.firstError.reason;
    this.banner.innerHTML = `<span class="vb-ic">✕</span> ${msg}`;
    this.banner.classList.remove('hidden');
  }

  _updateActionState() {
    const myTurn = this.game.isSelfTurn ? this.game.isSelfTurn() : this.game.isHumanTurn();
    this.el.querySelectorAll('.action-bar .btn').forEach((b) => {
      b.disabled = !myTurn;
    });
    const undoBtn = this.el.querySelector('[data-act="undo"]');
    // `canUndoTurn()` (no `moveManager.canUndo()`) para que funcione también en el INVITADO online,
    // que no tiene historial local (aplica el estado del host).
    if (undoBtn) undoBtn.disabled = !myTurn || !this.game.canUndoTurn();
    // CONTINUAR: SOLO se puede pulsar (y PULSA) cuando hay fichas jugadas y TODAS las palabras
    // del tablero son válidas. Si no, se DESHABILITA y deja de pulsar.
    const confirmBtn = this.el.querySelector('[data-act="confirm"]');
    if (confirmBtn) {
      const ready = myTurn && !!this.game.canConfirm?.();
      confirmBtn.disabled = !ready;
      confirmBtn.classList.toggle('ready', ready);
    }
  }

  showTurnScore(breakdown) {
    if (!breakdown) { this.turnScoreEl.innerHTML = ''; return; }
    const lines = breakdown.lines.map((l) => `<div class="ts-line"><span>${l.label}</span><span>+${l.points}</span></div>`).join('');
    this.turnScoreEl.innerHTML = `<div class="ts-box"><div class="ts-head">Turno: +${breakdown.total}</div>${lines}</div>`;
    setTimeout(() => { this.turnScoreEl.innerHTML = ''; }, 3500);
  }

  setTimer(seconds, visible) {
    // El reloj YA no está arriba a la derecha: se muestra DENTRO de la ficha del jugador
    // activo, oscureciendo su contenedor para que se lea bien la cuenta atrás.
    this.timerEl?.classList.add('hidden');
    const cards = this.playersPanel.querySelectorAll('.player-card');
    const activeIdx = this.game.state?.activePlayerIndex;
    const secs = Math.max(0, Math.ceil(seconds));
    const total = this.game.state?.timer?.seconds || 60;
    const ratio = Math.max(0, Math.min(1, seconds / total));
    const low = visible && seconds <= 10 && seconds > 0;
    cards.forEach((card, i) => {
      const on = visible && i === activeIdx;
      card.classList.toggle('timing', on);
      card.classList.toggle('time-low', on && low);
      const t = card.querySelector('.pc-timer');
      if (!t) return;
      t.classList.toggle('show', on);
      if (on) {
        t.querySelector('.pc-timer-num').textContent = String(secs);
        // Dibuja el anillo consumiéndose (dashoffset crece a medida que baja el tiempo).
        const prog = t.querySelector('.pctr-prog');
        if (prog) prog.style.strokeDashoffset = String(this._pcRingC * (1 - ratio));
      }
    });

    // Reloj GRANDE central: SOLO en los últimos 10 s Y en el turno del jugador local
    // (no en turnos de IA/rival). Se dibuja DENTRO de la escena 3D (por detrás de las
    // fichas del tablero, sobre el fondo) — lo pinta SceneManager al recibir este evento.
    const selfTurn = this.game.isSelfTurn ? this.game.isSelfTurn() : !this.game.players?.[activeIdx]?.isAI;
    this.bus.emit('hud:bigtimer', { show: low && selfTurn, secs, ratio });
  }

  show() {
    this.el.classList.remove('hidden');
    this.refresh();
  }
  hide() {
    this.el.classList.add('hidden');
  }
  dispose() {
    this._offState?.();
    this._offValid?.();
    this._offTurn?.();
  }
}
